import React, { useState, useEffect } from "react";
import { FiLink } from "react-icons/fi";
import Modal from "newui/components/ui/Modal/Modal";
import { cn } from "utils/helpers";
import TextBoxHTML from "newui/components/TextBoxHTML";
import type { ReviewClarification } from "../../../../types/clarification-review.type";

interface DuplicateSelectionModalProps {
	clarifications: ReviewClarification[];
	isLoading: boolean;
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (mainId: number, duplicateIds: number[]) => void;
}

export const DuplicateSelectionModal: React.FC<DuplicateSelectionModalProps> = ({
	isOpen,
	clarifications,
	onClose,
	onConfirm,
	isLoading,
}) => {
	const [mainId, setMainId] = useState<number | null>(null);

	useEffect(() => {
		if (isOpen && clarifications.length > 0) {
			setMainId(clarifications[0].id);
		}
	}, [isOpen, clarifications]);

	const handleConfirm = () => {
		if (!mainId) {
			return;
		}
		const duplicateIds = clarifications
			.filter((c) => c.id !== mainId)
			.map((c) => c.id);
		onConfirm(mainId, duplicateIds);
	};

	if (clarifications.length < 2) {
		return null;
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Link Duplicate Clarifications"
			size="lg"
			modalType="standard"
			iconType="info"
			closeOnEsc
			closeOnOverlayClick={false}
			primaryButton={{
				text: isLoading ? "Linking..." : "Link as Duplicates",
				onClick: handleConfirm,
				btntype: "primary",
				disabled: isLoading || !mainId,
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: onClose,
			}}
		>
			<div className="space-y-4">
				<div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
					<div className="flex items-center gap-2 mb-1">
						<FiLink size={14} className="text-blue-600" />
						<p className="text-sm font-medium text-blue-800">
							Select the main clarification
						</p>
					</div>
					<p className="text-xs text-blue-700">
						The main clarification will be kept and sent to the supplier.
						All others will be auto-declined as duplicates and linked to it.
					</p>
				</div>

				<div className="space-y-2">
					{clarifications.map((c) => {
						const isMain = mainId === c.id;
						return (
							<label
								key={c.id}
								className={cn(
									"flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors",
									isMain
										? "border-blue-300 bg-blue-50"
										: "border-gray-200 hover:bg-gray-50"
								)}
								data-testid={`duplicate-option-${c.id}`}
							>
								<input
									type="radio"
									name="main-clarification"
									value={c.id}
									checked={isMain}
									onChange={() => setMainId(c.id)}
									className="mt-1 text-blue-600"
								/>
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2 mb-1">
										<span className="text-xs font-mono text-gray-500">
											#{c.reference_number}
										</span>
										<span className="text-xs text-gray-400">
											{c.supplier_name}
										</span>
										{isMain && (
											<span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
												Main
											</span>
										)}
									</div>
									<TextBoxHTML
										className="text-sm text-gray-700 line-clamp-2"
										html={c.clarification_text}
									/>
									{c.created_by && (
										<p className="mt-1 text-xs text-gray-400">
											Proposed by {c.created_by.name}
										</p>
									)}
								</div>
							</label>
						);
					})}
				</div>
			</div>
		</Modal>
	);
};
